import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";

import { retrieveAllItemsApi, retrieveItemByIdApi } from "../../../components/api/ItemApiService";
import { retrieveFastOffersApi, retrieveFilteredOffersApi } from "../../../components/api/OfferApiService";
import { retrieveFastOrdersApi, retrieveFilteredOrdersApi } from "../../../components/api/OrderApiService";
import { retrieveFastShipmentRequestsApi } from "../../../components/api/ShipmentRequestApiService";
import { retrieveFastShippingInstructionsApi } from "../../../components/api/ShippingInstructionApiService";

const SOURCE_TYPES = [
  { value: "ITEM", label: "Item" },
  { value: "CUSTOMER_OFFER", label: "Customer Offer" },
  { value: "VENDOR_OFFER", label: "Vendor Offer" },
  { value: "SALES_ORDER", label: "Sales Order" },
  { value: "PURCHASE_ORDER", label: "Purchase Order" },
  { value: "SHIPMENT_REQUEST", label: "Shipment Request" },
  { value: "SHIPPING_INSTRUCTION", label: "Shipping Instruction" },
];

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (v) => (toNum(v) != null ? Number(v).toFixed(4) : "-");

const hasValue = (obj, ...keys) =>
  keys.some((k) => obj?.[k] != null && obj?.[k] !== "");

const stakeholderName = (row) =>
  row?.stakeholder?.name ||
  row?.offer?.stakeholder?.name ||
  row?.stakeholderName ||
  "-";

const diffState = (value, master) => {
  const v = toNum(value);
  const m = toNum(master);
  if (v == null || m == null) return "NA";
  return Math.abs(v - m) > 0.00001 ? "DIFF" : "MATCH";
};

const normalizeActive = (rows = []) => rows.filter((r) => String(r?.recordStatus ?? "ACTIVE").toUpperCase() !== "PASSIVE");

const getItemIdFromEntity = (type, row) => {
  if (!row) return null;
  if (type === "ITEM") return row?.id ?? null;
  if (type === "CUSTOMER_OFFER" || type === "VENDOR_OFFER") {
    return row?.offerItemId ?? row?.itemId ?? row?.item?.id ?? null;
  }
  if (type === "SALES_ORDER" || type === "PURCHASE_ORDER") {
    return row?.offerItemId ?? row?.itemId ?? row?.offer?.item?.id ?? null;
  }
  if (type === "SHIPMENT_REQUEST") {
    return (
      row?.salesOrderItemId ??
      row?.purchaseOrderItemId ??
      row?.salesOrder?.offer?.item?.id ??
      row?.purchaseOrder?.offer?.item?.id ??
      null
    );
  }
  return (
    row?.salesOrderItemId ??
    row?.purchaseOrderItemId ??
    row?.shipmentRequest?.salesOrder?.offer?.item?.id ??
    row?.shipmentRequest?.purchaseOrder?.offer?.item?.id ??
    null
  );
};

const getItemMetaFromEntity = (type, row) => {
  if (!row) return { id: null, buyingGrade: null, origin: null, crop: null };

  if (type === "ITEM") {
    return {
      id: row?.id ?? null,
      buyingGrade: row?.buyingGrade ?? null,
      origin: row?.origin ?? null,
      crop: row?.crop ?? null,
    };
  }

  if (type === "CUSTOMER_OFFER" || type === "VENDOR_OFFER") {
    return {
      id: row?.offerItemId ?? row?.itemId ?? row?.item?.id ?? null,
      buyingGrade: row?.offerItemBuyingGrade ?? row?.itemBuyingGrade ?? row?.item?.buyingGrade ?? null,
      origin: row?.offerItemOrigin ?? row?.itemOrigin ?? row?.item?.origin ?? null,
      crop: row?.offerItemCrop ?? row?.itemCrop ?? row?.item?.crop ?? null,
    };
  }

  if (type === "SALES_ORDER" || type === "PURCHASE_ORDER") {
    return {
      id: row?.offerItemId ?? row?.itemId ?? row?.offer?.item?.id ?? null,
      buyingGrade: row?.offerItemBuyingGrade ?? row?.itemBuyingGrade ?? row?.offer?.item?.buyingGrade ?? null,
      origin: row?.offerItemOrigin ?? row?.itemOrigin ?? row?.offer?.item?.origin ?? null,
      crop: row?.offerItemCrop ?? row?.itemCrop ?? row?.offer?.item?.crop ?? null,
    };
  }

  if (type === "SHIPMENT_REQUEST") {
    return {
      id: row?.salesOrderItemId ?? row?.purchaseOrderItemId ?? row?.salesOrder?.offer?.item?.id ?? row?.purchaseOrder?.offer?.item?.id ?? null,
      buyingGrade:
        row?.salesOrderItemBuyingGrade ??
        row?.purchaseOrderItemBuyingGrade ??
        row?.salesOrder?.offer?.item?.buyingGrade ??
        row?.purchaseOrder?.offer?.item?.buyingGrade ??
        null,
      origin:
        row?.salesOrderItemOrigin ??
        row?.purchaseOrderItemOrigin ??
        row?.salesOrder?.offer?.item?.origin ??
        row?.purchaseOrder?.offer?.item?.origin ??
        null,
      crop:
        row?.salesOrderItemCrop ??
        row?.purchaseOrderItemCrop ??
        row?.salesOrder?.offer?.item?.crop ??
        row?.purchaseOrder?.offer?.item?.crop ??
        null,
    };
  }

  return {
    id:
      row?.salesOrderItemId ??
      row?.purchaseOrderItemId ??
      row?.shipmentRequest?.salesOrder?.offer?.item?.id ??
      row?.shipmentRequest?.purchaseOrder?.offer?.item?.id ??
      null,
    buyingGrade:
      row?.salesOrderItemBuyingGrade ??
      row?.purchaseOrderItemBuyingGrade ??
      row?.shipmentRequest?.salesOrder?.offer?.item?.buyingGrade ??
      row?.shipmentRequest?.purchaseOrder?.offer?.item?.buyingGrade ??
      null,
    origin:
      row?.salesOrderItemOrigin ??
      row?.purchaseOrderItemOrigin ??
      row?.shipmentRequest?.salesOrder?.offer?.item?.origin ??
      row?.shipmentRequest?.purchaseOrder?.offer?.item?.origin ??
      null,
    crop:
      row?.salesOrderItemCrop ??
      row?.purchaseOrderItemCrop ??
      row?.shipmentRequest?.salesOrder?.offer?.item?.crop ??
      row?.shipmentRequest?.purchaseOrder?.offer?.item?.crop ??
      null,
  };
};

const buildItemMetaLabel = (type, row) => {
  const meta = getItemMetaFromEntity(type, row);
  return `Item ${meta?.id ?? "-"} · Grade ${meta?.buyingGrade ?? "-"} · Origin ${meta?.origin ?? "-"} · Crop ${meta?.crop ?? "-"}`;
};

const getOptionLabel = (type, row) => {
  if (!row) return "";
  if (type === "ITEM") {
    return `${row.id || ""} - ${row.buyingGrade || ""} - Origin ${row?.origin || "-"} - Crop ${row?.crop || "-"}`;
  }
  if (type === "CUSTOMER_OFFER" || type === "VENDOR_OFFER") {
    return `${row.id || ""} - ${row.offerNumber || ""} - ${buildItemMetaLabel(type, row)}`;
  }
  if (type === "SALES_ORDER" || type === "PURCHASE_ORDER") {
    const sapNo = type === "SALES_ORDER"
      ? (row?.salesOrderNumber || row?.orderNumber || "-")
      : (row?.purchaseOrderNumber || row?.orderNumber || "-");
    const sapLabel = type === "SALES_ORDER" ? "SAP SO" : "SAP PO";
    return `${row.id || ""} - ${sapLabel} ${sapNo} - ${buildItemMetaLabel(type, row)}`;
  }
  if (type === "SHIPMENT_REQUEST") {
    return `${row.id || ""} - ${row.shipmentRequestType || ""} - ${buildItemMetaLabel(type, row)}`;
  }
  return `${row.id || ""} - ${row.finalShippingInstructionNumber || ""} - ${row.shipmentRequestType || ""} - ${buildItemMetaLabel(type, row)}`;
};

const sectionSx = {
  border: "1px solid #b0c8dc",
  padding: "6px 12px",
  fontSize: "0.76rem",
  fontWeight: 700,
  color: "#ffffff",
  backgroundColor: "#1976d2",
  letterSpacing: "0.03em",
};

const labelSx = {
  border: "1px solid #dde7ef",
  padding: "5px 12px",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "#2c3e50",
  backgroundColor: "#f7fafc",
  whiteSpace: "nowrap",
};

const refCellSx = {
  border: "1px solid #dde7ef",
  padding: "4px 10px",
  fontSize: "0.78rem",
  textAlign: "center",
  color: "#607d8b",
  backgroundColor: "#f0f4f7",
};

const valueCellSx = (value, ref) => {
  const state = diffState(value, ref);
  return {
    border: "1px solid #dde7ef",
    padding: "4px 10px",
    fontSize: "0.8rem",
    textAlign: "center",
    fontWeight: state === "DIFF" ? 700 : 400,
    color: state === "DIFF" ? "#b71c1c" : state === "MATCH" ? "#1b5e20" : "#444",
    backgroundColor: state === "DIFF" ? "#ffebee" : state === "MATCH" ? "#e8f5e9" : "transparent",
  };
};

export default function NicotineSugarReportView() {
  const [sourceType, setSourceType] = useState("ITEM");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [masterItem, setMasterItem] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const historyCacheRef = useRef(new Map());

  const loadSourceOptions = useCallback(async (type) => {
    setLoadingOptions(true);
    try {
      let data = [];
      if (type === "ITEM") {
        const res = await retrieveAllItemsApi();
        data = res?.data || [];
      } else if (type === "CUSTOMER_OFFER") {
        const res = await retrieveFastOffersApi("CUSTOMER");
        data = normalizeActive(res?.data || []);
      } else if (type === "VENDOR_OFFER") {
        const res = await retrieveFastOffersApi("VENDOR");
        data = normalizeActive(res?.data || []);
      } else if (type === "SALES_ORDER") {
        const res = await retrieveFastOrdersApi("CUSTOMER");
        data = normalizeActive(res?.data || []);
      } else if (type === "PURCHASE_ORDER") {
        const res = await retrieveFastOrdersApi("VENDOR");
        data = normalizeActive(res?.data || []);
      } else if (type === "SHIPMENT_REQUEST") {
        const res = await retrieveFastShipmentRequestsApi();
        data = normalizeActive(res?.data || []);
      } else {
        const res = await retrieveFastShippingInstructionsApi();
        data = normalizeActive(res?.data || []);
      }
      setSourceOptions(data);
    } catch {
      toast.error("Failed to load selector options.");
      setSourceOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const buildSectionRows = useCallback((item, offersCustomer, offersVendor, ordersSales, ordersPurchase) => {
    const rows = [];
    const section = (label) => rows.push({ type: "section", label });
    const data = (label, nicotine, sugar) => rows.push({ type: "data", label, nicotine, sugar });
    const empty = (label) => rows.push({ type: "empty", label });

    section("1 · Master Item (Physical Leaf Characteristics)");
    if (item) {
      data(
        `Item #${item?.id ?? "-"} · ${item?.buyingGrade ?? ""} · Origin: ${item?.origin ?? "-"} · Crop: ${item?.crop ?? "-"}`,
        item?.nicotine,
        item?.sugar
      );
    } else {
      empty("No item data available");
    }

    section("2 · Offer Values (Sales / Purchase)");
    if (offersCustomer.length) {
      offersCustomer.forEach((o) => {
        data(
          `Customer Offer #${o?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Sales Offer`,
          o?.salesNicotine,
          o?.salesSugar
        );
      });
    } else {
      empty("Customer Offer - not selected");
    }

    section("3 · Samples");
    let hasSample = false;
    offersCustomer.forEach((o) => {
      if (o?.firstSample) {
        hasSample = true;
        data(
          `Customer Offer #${o.firstSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Offer Sample`,
          o.firstSample?.salesNicotine,
          o.firstSample?.salesSugar
        );
      }
      if (o?.secondSample) {
        hasSample = true;
        data(
          `Customer Offer #${o.secondSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Trial Sample`,
          o.secondSample?.salesNicotine,
          o.secondSample?.salesSugar
        );
      }
    });
    offersVendor.forEach((o) => {
      if (o?.firstSample) {
        hasSample = true;
        data(
          `Vendor Offer #${o.firstSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Offer Sample`,
          o.firstSample?.salesNicotine,
          o.firstSample?.salesSugar
        );
      }
      if (o?.secondSample) {
        hasSample = true;
        data(
          `Vendor Offer #${o.secondSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Trial Sample`,
          o.secondSample?.salesNicotine,
          o.secondSample?.salesSugar
        );
      }
    });
    ordersSales.forEach((o) => {
      if (o?.firstSample) {
        hasSample = true;
        data(
          `SO Sample #${o.firstSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Redrawn Sample`,
          o.firstSample?.salesNicotine,
          o.firstSample?.salesSugar
        );
      }
    });
    ordersPurchase.forEach((o) => {
      if (o?.firstSample) {
        hasSample = true;
        data(
          `PO Sample #${o.firstSample?.id ?? "-"}  ·  Stakeholder: ${stakeholderName(o)}  ·  Redrawn Sample`,
          o.firstSample?.salesNicotine,
          o.firstSample?.salesSugar
        );
      }
    });
    if (!hasSample) {
      empty("Customer Offer Offer Sample - none");
      empty("Customer Offer Trial Sample - none");
      empty("SO Sample - none");
      empty("Vendor Offer Offer Sample - none");
      empty("Vendor Offer Trial Sample - none");
      empty("PO Sample - none");
    }

    section("4 · Packaging Labels (declared on order)");
    let hasPackaging = false;
    ordersSales.forEach((o) => {
      if (hasValue(o, "packagingNicotine", "packagingSugar")) {
        hasPackaging = true;
        data(
          `SO #${o?.id ?? "-"} · Stakeholder: ${stakeholderName(o)} · Packaging`,
          o?.packagingNicotine,
          o?.packagingSugar
        );
      }
    });
    ordersPurchase.forEach((o) => {
      if (hasValue(o, "packagingNicotine", "packagingSugar")) {
        hasPackaging = true;
        data(
          `PO #${o?.id ?? "-"} · Stakeholder: ${stakeholderName(o)} · Packaging`,
          o?.packagingNicotine,
          o?.packagingSugar
        );
      }
    });
    if (!hasPackaging) {
      empty("SO Packaging - not set");
      empty("PO Packaging - not set");
    }

    return rows;
  }, []);

  const loadHistoryForItem = useCallback(async (itemId) => {
    if (!itemId) {
      setMasterItem(null);
      setTableRows([]);
      return;
    }

    if (historyCacheRef.current.has(itemId)) {
      const cached = historyCacheRef.current.get(itemId);
      setMasterItem(cached.masterItem);
      setTableRows(cached.tableRows);
      return;
    }

    setLoadingHistory(true);
    try {
      const offerCustomerFilter = {
        id: 0,
        offerType: "CUSTOMER",
        stakeHolderType: "CUSTOMER",
        itemIdList: [itemId],
        recordStatusList: ["ACTIVE"],
      };
      const offerVendorFilter = {
        id: 0,
        offerType: "VENDOR",
        stakeHolderType: "VENDOR",
        itemIdList: [itemId],
        recordStatusList: ["ACTIVE"],
      };
      const orderSalesFilter = {
        id: 0,
        orderType: "CUSTOMER",
        stakeHolderType: "CUSTOMER",
        itemIdList: [itemId],
        recordStatusList: ["ACTIVE"],
      };
      const orderPurchaseFilter = {
        id: 0,
        orderType: "VENDOR",
        stakeHolderType: "VENDOR",
        itemIdList: [itemId],
        recordStatusList: ["ACTIVE"],
      };

      const [
        itemRes,
        offersCustomerRes,
        offersVendorRes,
        ordersSalesRes,
        ordersPurchaseRes,
      ] = await Promise.all([
        retrieveItemByIdApi(itemId),
        retrieveFilteredOffersApi(offerCustomerFilter),
        retrieveFilteredOffersApi(offerVendorFilter),
        retrieveFilteredOrdersApi(orderSalesFilter),
        retrieveFilteredOrdersApi(orderPurchaseFilter),
      ]);

      const item = itemRes?.data || null;
      const offersCustomer = normalizeActive(offersCustomerRes?.data || []);
      const offersVendor = normalizeActive(offersVendorRes?.data || []);
      const ordersSales = normalizeActive(ordersSalesRes?.data || []);
      const ordersPurchase = normalizeActive(ordersPurchaseRes?.data || []);

      const rows = buildSectionRows(item, offersCustomer, offersVendor, ordersSales, ordersPurchase);

      setMasterItem(item);
      setTableRows(rows);
      historyCacheRef.current.set(itemId, { masterItem: item, tableRows: rows });
    } catch {
      toast.error("Failed to load nicotine/sugar history.");
      setMasterItem(null);
      setTableRows([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [buildSectionRows]);

  useEffect(() => {
    setSelectedSource(null);
    setMasterItem(null);
    setTableRows([]);
    loadSourceOptions(sourceType);
  }, [sourceType, loadSourceOptions]);

  const selectedItemId = useMemo(() => getItemIdFromEntity(sourceType, selectedSource), [sourceType, selectedSource]);

  useEffect(() => {
    loadHistoryForItem(selectedItemId);
  }, [selectedItemId, loadHistoryForItem]);

  const masterNic = masterItem?.nicotine;
  const masterSug = masterItem?.sugar;

  const exportReport = useCallback(async () => {
    if (!selectedItemId || !masterItem || !tableRows.length) {
      toast.error("No report data to export.");
      return;
    }

    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "San International";
      wb.created = new Date();

      const ws = wb.addWorksheet("Nicotine-Sugar Report");
      ws.columns = [
        { key: "source", width: 70 },
        { key: "nicotine", width: 14 },
        { key: "masterNic", width: 14 },
        { key: "sugar", width: 14 },
        { key: "masterSugar", width: 14 },
        { key: "nicStatus", width: 13 },
        { key: "sugStatus", width: 13 },
      ];

      ws.mergeCells("A1:G1");
      ws.getCell("A1").value = "Nicotine-Sugar Report";
      ws.getCell("A1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
      ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };
      ws.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1565C0" } };

      ws.mergeCells("A2:G2");
      ws.getCell("A2").value = `Generated: ${new Date().toLocaleString()}`;
      ws.getCell("A2").font = { italic: true, color: { argb: "FF455A64" } };

      ws.getCell("A4").value = "Source Type";
      ws.getCell("B4").value = SOURCE_TYPES.find((x) => x.value === sourceType)?.label || sourceType;
      ws.getCell("D4").value = "Selected Record";
      ws.getCell("E4").value = selectedSource?.id ?? "-";

      ws.getCell("A5").value = "Master Item";
      ws.getCell("B5").value = masterItem?.id ?? "-";
      ws.getCell("D5").value = "Buying Grade";
      ws.getCell("E5").value = masterItem?.buyingGrade ?? "-";

      ws.getCell("A6").value = "Origin";
      ws.getCell("B6").value = masterItem?.origin ?? "-";
      ws.getCell("D6").value = "Crop";
      ws.getCell("E6").value = masterItem?.crop ?? "-";

      ws.getCell("A7").value = "Master Nicotine";
      ws.getCell("B7").value = toNum(masterNic);
      ws.getCell("D7").value = "Master Sugar";
      ws.getCell("E7").value = toNum(masterSug);
      ws.getCell("B7").numFmt = "0.0000";
      ws.getCell("E7").numFmt = "0.0000";

      ["A4", "A5", "A6", "A7", "D4", "D5", "D6", "D7"].forEach((addr) => {
        ws.getCell(addr).font = { bold: true, color: { argb: "FF263238" } };
      });

      const headerRowIndex = 9;
      ws.getRow(headerRowIndex).values = [
        "Source",
        "Nicotine",
        "Master Nic",
        "Sugar",
        "Master Sugar",
        "Nic Status",
        "Sugar Status",
      ];
      ws.getRow(headerRowIndex).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(headerRowIndex).alignment = { horizontal: "center", vertical: "middle" };
      ws.getRow(headerRowIndex).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };

      let rowIdx = headerRowIndex + 1;
      tableRows.forEach((r) => {
        if (r.type === "section") {
          ws.mergeCells(`A${rowIdx}:G${rowIdx}`);
          ws.getCell(`A${rowIdx}`).value = r.label;
          ws.getCell(`A${rowIdx}`).font = { bold: true, color: { argb: "FFFFFFFF" } };
          ws.getCell(`A${rowIdx}`).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1976D2" } };
          ws.getCell(`A${rowIdx}`).alignment = { horizontal: "left", vertical: "middle" };
          rowIdx += 1;
          return;
        }

        if (r.type === "empty") {
          ws.getCell(`A${rowIdx}`).value = r.label;
          ws.mergeCells(`B${rowIdx}:G${rowIdx}`);
          ws.getCell(`B${rowIdx}`).value = "- not available -";
          ws.getCell(`B${rowIdx}`).font = { italic: true, color: { argb: "FF90A4AE" } };
          ws.getCell(`B${rowIdx}`).alignment = { horizontal: "center" };
          rowIdx += 1;
          return;
        }

        const nicVal = toNum(r.nicotine);
        const sugVal = toNum(r.sugar);
        const nicState = diffState(r.nicotine, masterNic);
        const sugState = diffState(r.sugar, masterSug);

        ws.getRow(rowIdx).values = [
          r.label,
          nicVal,
          toNum(masterNic),
          sugVal,
          toNum(masterSug),
          nicState,
          sugState,
        ];

        ws.getCell(`B${rowIdx}`).numFmt = "0.0000";
        ws.getCell(`C${rowIdx}`).numFmt = "0.0000";
        ws.getCell(`D${rowIdx}`).numFmt = "0.0000";
        ws.getCell(`E${rowIdx}`).numFmt = "0.0000";
        ws.getCell(`F${rowIdx}`).alignment = { horizontal: "center" };
        ws.getCell(`G${rowIdx}`).alignment = { horizontal: "center" };

        if (nicState === "DIFF") {
          ws.getCell(`B${rowIdx}`).font = { bold: true, color: { argb: "FFB71C1C" } };
          ws.getCell(`F${rowIdx}`).font = { bold: true, color: { argb: "FFB71C1C" } };
        }
        if (sugState === "DIFF") {
          ws.getCell(`D${rowIdx}`).font = { bold: true, color: { argb: "FFB71C1C" } };
          ws.getCell(`G${rowIdx}`).font = { bold: true, color: { argb: "FFB71C1C" } };
        }

        rowIdx += 1;
      });

      for (let r = headerRowIndex; r < rowIdx; r += 1) {
        for (let c = 1; c <= 7; c += 1) {
          const cell = ws.getRow(r).getCell(c);
          cell.border = {
            top: { style: "thin", color: { argb: "FFD0D7DE" } },
            left: { style: "thin", color: { argb: "FFD0D7DE" } },
            bottom: { style: "thin", color: { argb: "FFD0D7DE" } },
            right: { style: "thin", color: { argb: "FFD0D7DE" } },
          };
        }
      }

      ws.views = [{ state: "frozen", ySplit: headerRowIndex }];
      ws.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: 7 },
      };

      const buffer = await wb.xlsx.writeBuffer();
      const safeItem = String(masterItem?.id ?? "item").replace(/[^a-zA-Z0-9_-]/g, "_");
      const filename = `NicotineSugarReport_${safeItem}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
      );
      toast.success("Report exported successfully.");
    } catch {
      toast.error("Failed to export report.");
    } finally {
      setExporting(false);
    }
  }, [masterItem, masterNic, masterSug, selectedItemId, selectedSource, sourceType, tableRows]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Nicotine-Sugar Report</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            Pick any record type, choose one record, and review nicotine-sugar history in section format.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
         <IconButton onClick={exportReport} disabled={exporting || loadingHistory || !selectedItemId || tableRows.length === 0} color="primary" size="large">
              <DownloadIcon />
            </IconButton>
          <Tooltip title="Reload selector options">
            <IconButton onClick={() => loadSourceOptions(sourceType)} disabled={loadingOptions} color="primary" size="large">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={SOURCE_TYPES}
              value={SOURCE_TYPES.find((o) => o.value === sourceType) || null}
              onChange={(_, v) => setSourceType(v?.value || "ITEM")}
              getOptionLabel={(o) => o?.label || ""}
              renderInput={(params) => <TextField {...params} label="Source Type" />}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Autocomplete
              size="small"
              options={sourceOptions}
              value={selectedSource}
              loading={loadingOptions}
              onChange={(_, v) => setSelectedSource(v || null)}
              getOptionLabel={(option) => getOptionLabel(sourceType, option)}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => <TextField {...params} label="Select Record" />}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={3}>
            <Typography variant="caption" color="text.secondary">Master Item ID</Typography>
            <Typography variant="body2" fontWeight={700}>{masterItem?.id ?? "-"}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Buying Grade</Typography>
            <Typography variant="body2" fontWeight={700}>{masterItem?.buyingGrade ?? "-"}</Typography>
          </Grid>
          <Grid item xs={6} md={2.5}>
            <Typography variant="caption" color="text.secondary">Master Nicotine</Typography>
            <Typography variant="body2" fontWeight={700}>{fmt(masterNic)}</Typography>
          </Grid>
          <Grid item xs={6} md={2.5}>
            <Typography variant="caption" color="text.secondary">Master Sugar</Typography>
            <Typography variant="body2" fontWeight={700}>{fmt(masterSug)}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Origin</Typography>
            <Typography variant="body2" fontWeight={700}>{masterItem?.origin ?? "-"}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Crop</Typography>
            <Typography variant="body2" fontWeight={700}>{masterItem?.crop ?? "-"}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap", bgcolor: "action.selected" } }}>
                <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "42%", fontSize: "0.76rem" }}>Source</TableCell>
                <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "14.5%", textAlign: "center", fontSize: "0.76rem" }}>Nicotine</TableCell>
                <TableCell sx={{ ...sectionSx, backgroundColor: "#546e7a", width: "14.5%", textAlign: "center", fontSize: "0.76rem" }}>↑ Master Nic</TableCell>
                <TableCell sx={{ ...sectionSx, backgroundColor: "#37474f", width: "14.5%", textAlign: "center", fontSize: "0.76rem" }}>Sugar</TableCell>
                <TableCell sx={{ ...sectionSx, backgroundColor: "#546e7a", width: "14.5%", textAlign: "center", fontSize: "0.76rem" }}>↑ Master Sugar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHistory ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">Loading history...</Typography>
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">Select a source record to view nicotine/sugar history.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((r, idx) => {
                  if (r.type === "section") {
                    return (
                      <TableRow key={`sec-${idx}`}>
                        <TableCell colSpan={5} sx={sectionSx}>{r.label}</TableCell>
                      </TableRow>
                    );
                  }

                  if (r.type === "empty") {
                    return (
                      <TableRow key={`emp-${idx}`}>
                        <TableCell sx={labelSx}>{r.label}</TableCell>
                        <TableCell colSpan={4} sx={{ border: "1px solid #dde7ef", textAlign: "center", color: "#b0bec5", fontSize: "0.76rem", fontStyle: "italic" }}>
                          - not available -
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={`row-${idx}`} hover>
                      <TableCell sx={labelSx}>{r.label}</TableCell>
                      <TableCell sx={valueCellSx(r.nicotine, masterNic)}>{fmt(r.nicotine)}</TableCell>
                      <TableCell sx={refCellSx}>{fmt(masterNic)}</TableCell>
                      <TableCell sx={valueCellSx(r.sugar, masterSug)}>{fmt(r.sugar)}</TableCell>
                      <TableCell sx={refCellSx}>{fmt(masterSug)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
